import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import {
  Dispatch,
  DispatchWithoutAction,
  SetStateAction,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { Icons, ItemImageArea } from 'utoniq-ui'

import {
  ConsoleUserOwnedItemsPortfolioDetailFragment,
  ItemType,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ItemIcon } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { ArtistThemeProvider } from '@/components/styles'
import { useTranslation } from '@/core'
import { useRouter } from '@/core'
import { Template } from '@/core/architecture'

import type { Action, State } from '../viewModel'

export * from './fragments'

type FilterStatus = null | 'used' | 'unused'

export const ConsoleUserOwnedItemsTemplate: Template<State, Action> = ({
  state,
  onFilter,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleUserOwnedItems')
  const [filterTicket, toggleFilterTicket] = useReducer(v => {
    onFilter(v ? 'general' : ItemType.Ticket)
    return !v
  }, true)

  const [filterStatus, setFilterStatus] = useState<FilterStatus>(null)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          back: true,
          title: t('title', '所有アイテム一覧'),
        }}
        sx={{
          px: 5,
        }}
      >
        <Stack
          sx={{ mt: 5, flexDirection: 'row', alignItems: 'center', gap: 2 }}
        >
          <FilterMenu
            filterTicket={filterTicket}
            toggleFilterTicket={toggleFilterTicket}
          />
          <FilterStatus status={filterStatus} setStatus={setFilterStatus} />
        </Stack>
        <ItemsCell
          userId={state.userId}
          loading={state.loading}
          portfolios={state.portfolios}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          filterStatus={filterStatus}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const FilterMenu = ({
  filterTicket,
  toggleFilterTicket,
}: {
  filterTicket: boolean
  toggleFilterTicket: DispatchWithoutAction
}) => {
  const { t } = useTranslation('consoleUserOwnedItems')

  return (
    <Button
      onClick={toggleFilterTicket}
      variant="outlined"
      sx={{
        width: '68px',
        height: '28px',
        whiteSpace: 'nowrap',
        typography: 'subtitle1',
        ...(filterTicket
          ? {
              borderRadius: '14px',
              border: 'solid 2px',
              borderColor: 'primary.main',
              bgcolor: 'primary.light',
              color: 'text.primary',
            }
          : {
              border: 'solid 1px',
              borderColor: 'grey.100',
              color: 'text.hint',
            }),
      }}
    >
      {t('ticket')}
    </Button>
  )
}

const FilterStatus = ({
  status,
  setStatus,
}: {
  status: FilterStatus
  setStatus: Dispatch<SetStateAction<FilterStatus>>
}) => {
  const { t } = useTranslation('consoleUserOwnedItems')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const options = [
    {
      label: t('all'),
      onClick: () => {
        setStatus(null)
        setAnchorEl(null)
      },
    },
    {
      label: t('unused'),
      onClick: () => {
        setStatus('unused')
        setAnchorEl(null)
      },
    },
    {
      label: t('used'),
      onClick: () => {
        setStatus('used')
        setAnchorEl(null)
      },
    },
  ]

  return (
    <Box>
      <Button
        onClick={e => setAnchorEl(e.currentTarget)}
        variant="outlined"
        sx={{
          height: '28px',
          px: 2,
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...(status !== null
            ? {
                borderRadius: '14px',
                border: 'solid 2px',
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
                color: 'text.primary',
                typography: 'subtitle1',
              }
            : {
                border: 'solid 1px',
                borderColor: 'grey.100',
                color: 'text.hint',
              }),
        }}
      >
        {t(`status${status}`)}
        <Icons.DownArrow fontSize="xSmall" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {options.map(({ label, ...option }) => (
          <MenuItem {...option} sx={{ width: 200 }} key={label}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

type ItemsCellProps = {
  portfolios: State['portfolios']
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: Action['onFetchMore']
  filterStatus: FilterStatus
  userId: State['userId']
}

const ItemsCell = ({
  portfolios,
  loading,
  onFetchMore,
  hasNextPage,
  filterStatus,
  userId,
}: ItemsCellProps) => {
  const { t } = useTranslation('consoleUserOwnedItems')

  return (
    <Box sx={{ mt: 5, pb: 6 }}>
      <ScrollFetcher
        loading={loading}
        onFetchMore={onFetchMore}
        hasNextPage={!!hasNextPage}
        emptyMessage={t('emptyMessage')}
        // HACK: filterStatusで内部コンポーネントの表示非表示を制御しているため、余白の調整を行う
        componentProps={{ spacing: 0 }}
        nodes={portfolios?.edges.map(({ node }) => {
          if (node.item.metadata?.ticket?.throughTicket?.length) {
            return (
              <>
                {node.item.metadata.ticket.throughTicket.map(
                  (throughTicket, index) => (
                    <ItemBox
                      userId={userId}
                      filterStatus={filterStatus}
                      key={`${node.id}:${node.item?.sku?.id}:${index}`}
                      item={node.item}
                      serials={node.serials}
                      posessionNumber={node.number}
                      throughTicketIndex={index}
                    />
                  )
                )}
              </>
            )
          }
          return (
            <ItemBox
              userId={userId}
              filterStatus={filterStatus}
              key={`${node.id}:${node.item?.sku?.id}`}
              item={node.item}
              serials={node.serials}
              posessionNumber={node.number}
            />
          )
        })}
      />
    </Box>
  )
}

type ItemBoxProps = {
  item: ConsoleUserOwnedItemsPortfolioDetailFragment['item']
  serials: ConsoleUserOwnedItemsPortfolioDetailFragment['serials']
  posessionNumber: ConsoleUserOwnedItemsPortfolioDetailFragment['number']
  filterStatus: FilterStatus
  userId: State['userId']
  throughTicketIndex?: number
}

const ItemBox = ({
  item,
  serials,
  posessionNumber,
  filterStatus,
  userId,
  throughTicketIndex,
}: ItemBoxProps) => {
  const { t } = useTranslation('consoleUserOwnedItems')
  const { generatePath, routerPages } = useRouter()

  const thoughTicket = useMemo(() => {
    if (throughTicketIndex === undefined) {
      return undefined
    }
    return item.metadata?.ticket?.throughTicket?.[throughTicketIndex]
  }, [item.metadata?.ticket?.throughTicket, throughTicketIndex])

  // 使用済みのもの
  const usedNumber = useMemo(() => {
    if (!serials?.length) {
      return 0
    }
    if (throughTicketIndex !== undefined) {
      return serials.filter(
        ({ serial }) =>
          serial?.ticketStatus?.throughTicket?.[throughTicketIndex]?.isUsed
      ).length
    }
    return serials.filter(({ isUsed }) => isUsed).length
  }, [serials, throughTicketIndex])
  // すべて使用済みかどうか
  const isUsed = useMemo(() => {
    return posessionNumber === usedNumber
  }, [posessionNumber, usedNumber])

  const href = useMemo<string>(() => {
    const url = generatePath(routerPages.ConsoleUserOwnedItemDetail, {
      itemId: item.id,
      userId,
    })
    const searchParams = new URLSearchParams(
      //remove null and undefined value
      omitBy({ skuId: item.sku?.id, throughTicketIndex }, isNil) as never
    )
    return `${url}?${searchParams.toString()}`
  }, [generatePath, item.sku?.id, item.id, userId, throughTicketIndex])

  if (
    !item ||
    (filterStatus === 'unused' && isUsed) ||
    (filterStatus === 'used' && !isUsed)
  )
    return null

  return (
    <Box sx={{ pb: 4 }}>
      <Card elevation={0} sx={{ position: 'relative' }}>
        <CardActionArea href={href}>
          <CardContent>
            <Stack direction="row" spacing={4}>
              <Box className="item-thumbnail">
                <Stack
                  sx={{
                    zIndex: 1,
                    marginInline: 'auto',
                    pl: 1,
                    width: 104,
                    position: 'absolute',
                    top: 24,
                  }}
                >
                  <ItemIcon type={item.type} />
                </Stack>

                <ItemImageArea
                  size="small"
                  src={item.thumbnail?.front?.smallUri ?? ''}
                />
              </Box>
              <Stack flex={1} justifyContent="center">
                <Stack
                  className="item-info"
                  sx={{ flexDirection: 'row', gap: 2 }}
                >
                  {item.type === ItemType.Ticket && (
                    <Chip
                      color={isUsed ? 'default' : 'primary'}
                      label={
                        <Typography
                          variant="overline"
                          color={isUsed ? 'text.secondary' : 'white'}
                        >
                          {isUsed
                            ? t('statusused', '使用済')
                            : t('statusunused', '未使用')}
                        </Typography>
                      }
                      sx={{
                        p: 1,
                        height: 20,
                        '.MuiChip-label': { px: 0 },
                      }}
                    />
                  )}
                  {item.metadata?.ticket?.isThroughTicket && (
                    <Chip
                      color={'secondary'}
                      label={
                        <Typography variant="overline" color={'white'}>
                          {t('throughTicket', '通し券')}
                        </Typography>
                      }
                      sx={{
                        p: 1,
                        height: 20,
                        '.MuiChip-label': { px: 0 },
                      }}
                    />
                  )}
                </Stack>

                {!!item.preSale?.event.title && (
                  <Typography
                    sx={{ mt: 1, mb: -2 }}
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {item.preSale?.event.title}
                  </Typography>
                )}
                <Typography
                  variant="h6"
                  sx={{ ...styleSets.lineCamp(2), mt: 2 }}
                >
                  {item.title} {thoughTicket?.name}
                </Typography>
                <Typography variant="caption">{item.sku?.unitName}</Typography>
                {item.type === ItemType.Ticket ? (
                  <Typography
                    sx={{ mt: 1 }}
                    variant="body2"
                    color="text.secondary"
                  >
                    {t('statusunused', '未使用')}:{' '}
                    {posessionNumber - usedNumber}/{posessionNumber}枚
                  </Typography>
                ) : (
                  <Typography
                    sx={{ mt: 1 }}
                    variant="body2"
                    color="text.secondary"
                  >
                    {t('posessionNumber', '所持数')}: {posessionNumber}
                    {t('numberUnit', '枚')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  )
}
